// =============================================================================
// LP Express — Offer Stack (value-stack close) template content model.
//
// An "Offer Stack" landing page is a value-stack close sales page (ported from
// the template library's sales.offer-stack): a hero (eyebrow + headline +
// subhead + quick benefit bullets) → a short product intro (paragraphs + the
// brand's REAL product photo) → THE VALUE STACK (the centerpiece: individually
// valued rows that add up to a crossed-out total, then discounted to "today's
// price" + CTA) → fast-action bonuses → an honest scarcity/urgency line → a
// reviews wall (REAL reviews via the review picker) → a guarantee block → an
// FAQ accordion → a legal disclaimer → a sticky bottom CTA bar (mobile).
//
// The whole page's COPY is generated in a single shot by a dedicated Sim
// Studio workflow (SIM_LP_OFFER_STACK_URL — a placeholder id until the
// workflow is built), then SPLIT by the server into per-section
// generatedContent keys (osHero, osIntro, osStack, osBonuses, osScarcity,
// osReviews, osGuarantee, osFaq, osDisclaimer, osStickyCta — see
// `splitLpOfferStackContent`). Those keys back the ordered, reorderable
// section model in shared/lp-express-sections.ts (LP_OFFER_STACK_SECTION_TYPES),
// so the page flows through the SAME sections pipeline as the product-hero /
// problem-solution: getLpSections order, moveSection reorder, per-section
// hide/delete, and per-section copy regeneration (the single workflow re-runs
// and the server merges back ONLY the requested section). Copy is edited in
// place through the SAME generatedContent path-based `setText` ops the other
// templates use — e.g. ["osStack","rows",0,"name"].
//
// IMAGERY: there are NO AI image slots. The intro section shows the brand's
// REAL product photo (swappable only via the media picker/upload —
// mediaOverrides slot "osIntro:image" — never AI-generated).
//
// PRICES / VALUES — FTC-SAFE BY CONSTRUCTION: the Sim workflow NEVER outputs
// dollar amounts, values, totals, percentages or review counts. Instead:
//   • Stack row VALUES are seeded by the server from REAL product data at
//     generation time (a bundle's rows are its member SKUs with their real
//     prices; a non-bundle seeds a single row with the product's real price).
//     Rows remain fully editable in the builder.
//   • The stack TOTAL is computed at render time by summing the rows' parsed
//     values (never stored, never stale) and only shows when it beats today's
//     price.
//   • "Today's price" is derived at render time from the real product price +
//     any real active sale on the run (same derivation as the other
//     templates' buy boxes); the save line only appears when a real total /
//     discount exists.
//   • Bonuses carry NO "value" figures (the source template's bonus values
//     are exactly the kind of fabricated reference pricing that creates FTC
//     risk).
//   • Scarcity is a STATIC, editable urgency line — no live countdown timer
//     and no fabricated deadlines (the section is hideable like any other).
//   • Reviews are persona-voice editable placeholders, swappable for REAL
//     reviews via the standard review picker (reviewOverrides).
//
// This module is pure + dependency-free so it runs identically on the client
// (renderer + editor) and the server (generation/normalization).
//
// ─── Sim workflow contract (SIM_LP_OFFER_STACK_URL) ──────────────────────────
//
// INPUT (top-level fields on the Start block — called with wrapInput=false,
// identical to the quiz/listicle/advertorial/product-hero/problem-solution
// envelopes):
//   {
//     "brandInfo":      string  // serialized brand doctrine/profile
//     "personaInfo":    string  // serialized persona persuasion profile
//     "productInfo":    string  // serialized product/offer attributes
//                               // (includes bundle member SKUs when the
//                               // product is a bundle)
//     "narrativeAngle": string  // serialized narrative angle
//     "offerInfo":      string  // serialized offer/pricing/sale context
//     "feedback":       string  // optional free-form regeneration steering
//     "currentCopy":    string  // optional JSON of the CURRENT page copy
//                               // (set on regeneration so the workflow
//                               // refines instead of starting from scratch)
//   }
//
// OUTPUT: a single JSON object (may be ```json-fenced under output.content —
// the extractor strips fences) matching LpOfferStackContent below. snake_case
// aliases are accepted for every camelCase key. Guidance for the workflow:
//   • Hero: a deal-framing `headline` ("everything you need, one price"), a
//     short `eyebrow` kicker, a `subhead`, and 3–4 `heroBullets`.
//   • `intro`: a heading + 1–3 short paragraphs on WHY it's bundled/stacked.
//   • `stack`: a "here's everything you get" `heading`, one-line `subhead`,
//     3–8 `rows` — each row is ONLY a `name` describing one component of the
//     offer (NO `value` — the server fills real prices), and a `ctaText`.
//   • `bonuses`: heading + 1–3 items ({ title, body }) — ONLY bonuses that
//     plausibly exist as part of the offer (guides, digital extras). NO value
//     figures.
//   • `scarcity`: a single honest `line` — availability/positioning framing
//     only; NEVER a fabricated deadline, countdown, or stock count.
//   • `reviews.items`: 2–3 testimonial cards ({ name, role, text, rating })
//     in persona voice — clearly editable placeholders operators replace
//     with REAL reviews via the review picker.
//   • `guarantee`: sealTop/sealBottom (e.g. "60" / "Day Guarantee"),
//     heading, body.
//   • `faq.items`: 4–6 Q&A pairs.
//   • Do NOT put prices, values, totals, percentages, review counts or dollar
//     amounts in ANY copy field — those are derived from real data at render
//     time or omitted.
// =============================================================================

export interface LpOfferStackRow {
  name: string;
  value: string; // e.g. "$249" — seeded from REAL product data ("" = no value shown)
}

export interface LpOfferStackBonus {
  title: string;
  body: string;
}

// One review card. Generated in persona voice by the Sim workflow and fully
// editable in the builder. When the brand has REAL review data, each card can
// be swapped for a real review via the standard review picker (the picked
// review is snapshotted on reviewOverrides and wins over the blob item).
export interface LpOfferStackReview {
  name: string;
  role: string; // e.g. "Verified buyer" ("" = none)
  text: string;
  rating: number; // 1–5 stars
}

export interface LpOfferStackFaq {
  q: string;
  a: string;
}

export interface LpOfferStackContent {
  eyebrow: string; // small kicker above the headline ("" = none)
  headline: string; // deal-framing hero headline
  subhead: string; // short sub-line ("" = none)
  heroBullets: string[]; // quick hero bullets (up to 5)
  intro: { heading: string; paragraphs: string[] };
  stack: {
    heading: string;
    subhead: string;
    rows: LpOfferStackRow[];
    ctaText: string;
  };
  bonuses: { heading: string; subhead: string; items: LpOfferStackBonus[] };
  scarcity: { line: string };
  reviews: { heading: string; items: LpOfferStackReview[] };
  guarantee: {
    sealTop: string;
    sealBottom: string;
    heading: string;
    body: string;
  };
  faq: { heading: string; items: LpOfferStackFaq[] };
  disclaimer: string;
  stickyCtaText: string; // sticky bottom bar CTA label
}

// ── Normalization ────────────────────────────────────────────────────────────

function asObj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : fallback;
}

function strArray(v: unknown, cap = 20): string[] {
  return Array.isArray(v)
    ? v.filter((x) => typeof x === "string" && (x as string).trim()).slice(0, cap)
    : [];
}

// Accept either a plain string array or the template-library's wrapped
// { items: [...] } / { paragraphs: [...] } object.
function itemsArray(v: unknown, cap: number): string[] {
  if (Array.isArray(v)) return strArray(v, cap);
  const o = asObj(v);
  return strArray(o.items ?? o.paragraphs, cap);
}

// Normalize a raw offer-stack payload (e.g. from the Sim workflow, which may
// use snake_case) into the canonical LpOfferStackContent shape. Missing fields
// become empty strings/arrays so the renderer/editor never crash — the caller
// decides whether an empty result counts as a generation failure
// (isLpOfferStackContentUsable). IMPORTANT: any new editable field must be
// added BOTH here and to the interfaces above, or the renderer will drop it.
export function normalizeLpOfferStackContent(raw: unknown): LpOfferStackContent {
  const o = asObj(raw);
  const meta = asObj(o.meta);

  const inSrc = asObj(o.intro);
  const intro = {
    heading: str(inSrc.heading),
    paragraphs: itemsArray(inSrc.paragraphs ?? inSrc.body, 4),
  };

  const stSrc = asObj(o.stack);
  const stack = {
    heading: str(stSrc.heading),
    subhead: str(stSrc.subhead ?? stSrc.dek),
    rows: (Array.isArray(stSrc.rows) ? stSrc.rows : [])
      .map((r) => {
        const ro = asObj(r);
        return {
          name: str(ro.name ?? ro.title ?? ro.item),
          value: str(ro.value ?? ro.price),
        };
      })
      .filter((r) => r.name)
      .slice(0, 10),
    ctaText: str(stSrc.ctaText ?? stSrc.cta_text) || "Get the deal",
  };

  const boSrc = asObj(o.bonuses);
  const bonuses = {
    heading: str(boSrc.heading),
    subhead: str(boSrc.subhead ?? boSrc.dek),
    items: (Array.isArray(boSrc.items) ? boSrc.items : [])
      .map((b) => {
        const bo = asObj(b);
        return {
          title: str(bo.title ?? bo.h ?? bo.heading),
          body: str(bo.body ?? bo.p ?? bo.text),
        };
      })
      .filter((b) => b.title || b.body)
      .slice(0, 4),
  };

  const scSrc = asObj(o.scarcity);
  const scarcity = { line: str(scSrc.line ?? scSrc.text ?? (typeof o.scarcity === "string" ? o.scarcity : "")) };

  const revSrc = asObj(o.reviews ?? o.reviews_block ?? o.reviewsBlock);
  const reviews = {
    heading: str(revSrc.heading),
    items: (Array.isArray(revSrc.items)
      ? revSrc.items
      : Array.isArray(revSrc.testimonials)
        ? revSrc.testimonials
        : Array.isArray(o.testimonials)
          ? o.testimonials
          : []
    )
      .map((t) => {
        const to = asObj(t);
        const ratingRaw = Number(to.rating ?? to.stars ?? 5);
        return {
          name: str(to.name ?? to.reviewer ?? to.author),
          role: str(to.role ?? to.title ?? to.meta),
          text: str(to.text ?? to.quote ?? to.review_text ?? to.reviewText),
          rating: Number.isFinite(ratingRaw)
            ? Math.max(1, Math.min(5, Math.round(ratingRaw)))
            : 5,
        };
      })
      .filter((t) => t.text)
      .slice(0, 3),
  };

  const guSrc = asObj(o.guarantee);
  const guarantee = {
    sealTop: str(guSrc.sealTop ?? guSrc.seal_top),
    sealBottom: str(guSrc.sealBottom ?? guSrc.seal_bottom),
    heading: str(guSrc.heading),
    body: str(guSrc.body ?? guSrc.text),
  };

  const faqSrc = asObj(o.faq);
  const faq = {
    heading: str(faqSrc.heading),
    items: (Array.isArray(faqSrc.items) ? faqSrc.items : [])
      .map((f) => {
        const fo = asObj(f);
        return {
          q: str(fo.q ?? fo.question),
          a: str(fo.a ?? fo.answer ?? fo.body),
        };
      })
      .filter((f) => f.q || f.a)
      .slice(0, 8),
  };

  const skSrc = asObj(o.sticky ?? o.sticky_bar ?? o.stickyBar);

  return {
    eyebrow: str(o.eyebrow ?? meta.eyebrow),
    headline: str(o.headline ?? meta.headline),
    subhead: str(o.subhead ?? o.dek ?? o.subheadline ?? meta.subhead),
    heroBullets: itemsArray(o.heroBullets ?? o.hero_bullets ?? o.bullets, 5),
    intro,
    stack,
    bonuses,
    scarcity,
    reviews,
    guarantee,
    faq,
    disclaimer: str(o.disclaimer),
    stickyCtaText:
      str(o.stickyCtaText ?? o.sticky_cta_text ?? str(skSrc.text)) ||
      stack.ctaText,
  };
}

// ── Per-section split / assembly ─────────────────────────────────────────────
// The single-workflow output is split into one generatedContent key per
// section block (matching LP_OFFER_STACK_SECTION_TYPES in
// shared/lp-express-sections.ts) so the page participates in the ordered
// sections model: reorder, hide/delete, and per-section copy regeneration.

// Hero copy slice.
export interface LpOfferStackHero {
  eyebrow: string;
  headline: string;
  subhead: string;
  heroBullets: string[];
}

// The per-section generatedContent keys, in canonical page order. Must stay in
// lockstep with LP_OFFER_STACK_SECTION_TYPES in shared/lp-express-sections.ts.
export const LP_OFFER_STACK_BLOCK_KEYS = [
  "osHero",
  "osIntro",
  "osStack",
  "osBonuses",
  "osScarcity",
  "osReviews",
  "osGuarantee",
  "osFaq",
  "osDisclaimer",
  "osStickyCta",
] as const;
export type LpOfferStackBlockKey = (typeof LP_OFFER_STACK_BLOCK_KEYS)[number];

// Split a normalized whole-page content object into its per-section
// generatedContent keys. Inverse of assembleLpOfferStackContent.
export function splitLpOfferStackContent(
  content: LpOfferStackContent,
): Record<LpOfferStackBlockKey, unknown> {
  const hero: LpOfferStackHero = {
    eyebrow: content.eyebrow,
    headline: content.headline,
    subhead: content.subhead,
    heroBullets: content.heroBullets,
  };
  return {
    osHero: hero,
    osIntro: content.intro,
    osStack: content.stack,
    osBonuses: content.bonuses,
    osScarcity: content.scarcity,
    osReviews: content.reviews,
    osGuarantee: content.guarantee,
    osFaq: content.faq,
    osDisclaimer: { text: content.disclaimer },
    osStickyCta: { ctaText: content.stickyCtaText },
  };
}

// Re-assemble the whole-page content shape from a generatedContent object that
// carries the per-section keys. Tolerant of missing/partial keys (returns
// normalized empties) — callers gate usability via
// isLpOfferStackContentUsable. Used by the renderer/editor to read one
// canonical shape, and by the server to build `currentCopy` for regeneration.
export function assembleLpOfferStackContent(gc: unknown): LpOfferStackContent {
  const o = asObj(gc);
  const hero = asObj(o.osHero);
  const disclaimer = asObj(o.osDisclaimer);
  const sticky = asObj(o.osStickyCta);
  return normalizeLpOfferStackContent({
    eyebrow: hero.eyebrow,
    headline: hero.headline,
    subhead: hero.subhead,
    heroBullets: hero.heroBullets,
    intro: o.osIntro,
    stack: o.osStack,
    bonuses: o.osBonuses,
    scarcity: o.osScarcity,
    reviews: o.osReviews,
    guarantee: o.osGuarantee,
    faq: o.osFaq,
    disclaimer: (disclaimer as { text?: unknown }).text,
    stickyCtaText: (sticky as { ctaText?: unknown }).ctaText,
  });
}

// True when a generatedContent object carries the per-section offer-stack keys
// with enough structure to render (see isLpOfferStackContentUsable).
export function hasUsableLpOfferStackSections(gc: unknown): boolean {
  const o = asObj(gc);
  if (!o.osHero) return false;
  return isLpOfferStackContentUsable(assembleLpOfferStackContent(o));
}

// True when a normalized offer-stack page carries enough structure to render a
// real value-stack close (a headline plus at least three substantive
// supporting blocks across the hero bullets, intro paragraphs, stack rows and
// FAQ). Used by the generator to reject an empty Sim response instead of
// persisting a blank page.
export function isLpOfferStackContentUsable(
  content: LpOfferStackContent | null | undefined,
): boolean {
  if (!content) return false;
  const hasHeadline = !!content.headline;
  const supporting =
    content.heroBullets.length +
    content.intro.paragraphs.length +
    content.stack.rows.length +
    content.faq.items.length;
  return hasHeadline && supporting >= 3;
}

// ── Render-time price derivation (FTC-safe) ──────────────────────────────────
// Parse a "$1,234.56"-style price text into a number, or null when no single
// parseable amount exists. Mirrors the tolerant parsing the renderer uses for
// real product priceText.
export function parseLpOfferStackValue(v: unknown): number | null {
  if (typeof v !== "string") return null;
  const m = v.replace(/,/g, "").match(/(\d+(?:\.\d{1,2})?)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Compute the crossed-out "total value" from the stack rows' REAL values.
// Returns null unless at least two rows carry parseable values (a single
// valued row is just the price — a "total" would be fake framing).
export function computeLpOfferStackTotal(rows: LpOfferStackRow[]): number | null {
  const vals = rows
    .map((r) => parseLpOfferStackValue(r.value))
    .filter((n): n is number => n !== null);
  if (vals.length < 2) return null;
  return Math.round(vals.reduce((a, b) => a + b, 0) * 100) / 100;
}

// ── AI intro-visual image slot ───────────────────────────────────────────────
// The intro (2nd) section's image is AI-generated through the standard
// single-slot image pipeline (SINGLE_SLOT_IMAGE_CONFIGS "offerStackImages" in
// server/services/lp-express-worker.ts): one Sim prompt call (philosophy
// image-prompt workflow by default; override via SIM_LP_OFFER_STACK_IMAGE_URL)
// + one Fal render anchored to the REAL product photo as the reference image.
// The media picker can still override the slot with a real product photo, and
// the real photo remains the render-time fallback when the slot is absent or
// failed (legacy pages keep working unchanged).
export const LP_OFFER_STACK_IMAGE_SLOTS: {
  key: string;
  variantHint: string;
}[] = [
  {
    key: "intro-visual",
    variantHint:
      "intro-visual — a warm, premium editorial lifestyle photograph of the product in believable real-world use context that matches the narrative angle (natural light, authentic setting, the product clearly recognizable as the hero of the frame, anchored to the reference product photo for exact appearance); NO text, NO words, NO labels, NO logos beyond what is on the product itself, no collages or split frames — one single clean photographic scene",
  },
];
